import { dataAproximada, validarAnimal, type AnimalCadastro } from '@equiview/domain';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BuscaAnimal } from '../components/BuscaAnimal.js';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  BarraAcoes,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  Campo,
  CampoTexto,
  classeCampo,
  EstadoVazio,
  OpcoesRadio,
  Painel,
  ResumoErros,
  Selecao,
  useAvisoSaida,
} from '../components/ui/index.js';
import { hojeIso, PELAGENS } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, AnimalEntrada, Haras, Sexo } from '../dados/fonte.js';

interface Formulario {
  name: string;
  abccmm_registry: string;
  stud_book: string;
  microchip: string;
  sex: Sexo | '';
  birth_date: string;
  birth_date_estimated: boolean;
  ano_estimado: string;
  pelagem: string;
  pelagem_outra: string;
  marcha_type_declared: 'batida' | 'picada' | 'nao_informada';
  owner_farm_id: string;
  criador: 'mesmo' | string;
  sire_id: string | null;
  dam_id: string | null;
  notes: string;
}

const novoFormulario = (harasId: string): Formulario => ({
  name: '',
  abccmm_registry: '',
  stud_book: '',
  microchip: '',
  sex: '',
  birth_date: '',
  birth_date_estimated: false,
  ano_estimado: '',
  pelagem: '',
  pelagem_outra: '',
  marcha_type_declared: 'nao_informada',
  owner_farm_id: harasId,
  criador: 'mesmo',
  sire_id: null,
  dam_id: null,
  notes: '',
});

function doAnimal(a: Animal): Formulario {
  const conhecida = (PELAGENS as readonly string[]).includes(a.coat_color ?? '');
  return {
    name: a.name,
    abccmm_registry: a.abccmm_registry ?? '',
    stud_book: a.stud_book ?? '',
    microchip: a.microchip ?? '',
    sex: a.sex,
    birth_date: a.birth_date_estimated ? '' : a.birth_date,
    birth_date_estimated: a.birth_date_estimated,
    ano_estimado: a.birth_date_estimated ? a.birth_date.slice(0, 4) : '',
    pelagem: a.coat_color ? (conhecida ? a.coat_color : 'outra') : '',
    pelagem_outra: a.coat_color && !conhecida ? a.coat_color : '',
    marcha_type_declared: a.marcha_type_declared ?? 'nao_informada',
    owner_farm_id: a.owner_farm_id ?? '',
    criador: !a.breeder_farm_id || a.breeder_farm_id === a.owner_farm_id ? 'mesmo' : a.breeder_farm_id,
    sire_id: a.sire_id,
    dam_id: a.dam_id,
    notes: a.notes ?? '',
  };
}

const anoValido = (ano: string) => /^\d{4}$/.test(ano) && Number(ano) >= 1950;

/** O formulario vira a linha de `animal`. */
function paraEntrada(f: Formulario, id: string | undefined): AnimalEntrada & { id?: string } {
  const nascimento = f.birth_date_estimated ? (anoValido(f.ano_estimado) ? dataAproximada(Number(f.ano_estimado)) : '') : f.birth_date;
  return {
    ...(id ? { id } : {}),
    name: f.name.trim(),
    abccmm_registry: f.abccmm_registry.trim() || null,
    stud_book: f.stud_book.trim() || null,
    microchip: f.microchip.trim() || null,
    sex: f.sex as Sexo,
    birth_date: nascimento,
    birth_date_estimated: f.birth_date_estimated,
    coat_color: (f.pelagem === 'outra' ? f.pelagem_outra.trim() : f.pelagem) || null,
    marcha_type_declared: f.marcha_type_declared === 'nao_informada' ? null : f.marcha_type_declared,
    owner_farm_id: f.owner_farm_id || null,
    breeder_farm_id: f.criador === 'mesmo' ? f.owner_farm_id || null : f.criador,
    sire_id: f.sire_id,
    dam_id: f.dam_id,
    notes: f.notes.trim() || null,
  };
}

export function FormAnimal() {
  const { id } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(
    async (f) => {
      const [haras, atual, animais] = await Promise.all([f.listarHaras(), f.haras(), f.listarAnimais()]);
      return { haras, atual, animais };
    },
    [],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ haras, atual, animais }) =>
        haras.length === 0 ? (
          <EstadoVazio
            titulo="Cadastre um haras primeiro"
            acao={
              <BotaoLink variante="principal" to="/haras/novo">
                Cadastrar haras
              </BotaoLink>
            }
          >
            Todo animal pertence a um haras.
          </EstadoVazio>
        ) : id && !animais.some((a) => a.id === id) ? (
          <EstadoVazio titulo="Animal não encontrado" acao={<BotaoLink to="/animais">Voltar ao plantel</BotaoLink>} />
        ) : (
          <Formulario key={id ?? 'novo'} id={id} haras={haras} atual={atual} animais={animais} />
        )
      }
    </Aguardar>
  );
}

function Formulario({
  id,
  haras,
  atual,
  animais,
}: {
  id: string | undefined;
  haras: Haras[];
  atual: Haras | null;
  animais: Animal[];
}) {
  const fonte = useFonte();
  const navegar = useNavigate();
  const editando = id ? animais.find((a) => a.id === id) : undefined;
  const inicial = editando ? doAnimal(editando) : novoFormulario(atual?.id ?? haras[0]!.id);
  const [f, setF] = useState<Formulario>(inicial);
  const [original, setOriginal] = useState(JSON.stringify(inicial));
  const [tentou, setTentou] = useState(false);
  const [confirmarAvisos, setConfirmarAvisos] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [cadastrados, setCadastrados] = useState<string[]>([]);
  const nome = useRef<HTMLInputElement>(null);
  const hoje = hojeIso();

  const sujo = JSON.stringify(f) !== original && !salvando;
  useAvisoSaida(sujo);

  const entrada = paraEntrada(f, id);
  const candidato: AnimalCadastro = {
    id: id ?? '__novo__',
    name: entrada.name,
    sex: f.sex,
    birth_date: entrada.birth_date,
    birth_date_estimated: entrada.birth_date_estimated,
    abccmm_registry: entrada.abccmm_registry,
    sire_id: entrada.sire_id,
    dam_id: entrada.dam_id,
  };
  const { erros: errosDominio, avisos } = validarAnimal(candidato, { todos: animais, hoje });
  const erros = [
    ...errosDominio,
    ...(f.birth_date_estimated && f.ano_estimado && !anoValido(f.ano_estimado)
      ? [{ campo: 'birth_date', mensagem: 'Informe o ano com 4 dígitos.' }]
      : []),
    ...(!f.owner_farm_id ? [{ campo: 'owner_farm_id', mensagem: 'Escolha o haras proprietário.' }] : []),
  ];
  // Registro repetido aparece na hora; o resto, depois da primeira tentativa de salvar.
  const erro = (campo: string) =>
    tentou || campo === 'abccmm_registry' ? erros.find((e) => e.campo === campo)?.mensagem : undefined;
  const mudar = <K extends keyof Formulario>(k: K, v: Formulario[K]) => {
    setF((atualF) => ({ ...atualF, [k]: v }));
    setConfirmarAvisos(false);
  };

  const salvar = async (outro: boolean) => {
    setTentou(true);
    if (erros.length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (avisos.length && !confirmarAvisos) {
      setConfirmarAvisos(true);
      return;
    }
    setSalvando(true);
    const novoId = await fonte.cadastro!.salvarAnimal(entrada);
    setSalvando(false);
    if (outro) {
      // Caminho rapido para cadastrar varios: limpa, mantem o haras, volta ao nome.
      const proximo = { ...novoFormulario(f.owner_farm_id), criador: f.criador };
      setCadastrados((c) => [...c, entrada.name]);
      setF(proximo);
      setOriginal(JSON.stringify(proximo));
      setTentou(false);
      setConfirmarAvisos(false);
      window.scrollTo({ top: 0 });
      requestAnimationFrame(() => nome.current?.focus());
      return;
    }
    setOriginal(JSON.stringify(f));
    navegar(`/animais/${novoId}`, { replace: !!id });
  };

  const arquivar = async () => {
    if (!id || !window.confirm(`Arquivar ${f.name}? O animal sai do plantel, mas o registro continua guardado.`)) return;
    setOriginal(JSON.stringify(f));
    await fonte.cadastro!.arquivarAnimal(id);
    navegar('/animais', { replace: true });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void salvar(false);
      }}
      className="space-y-6"
      noValidate
    >
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Plantel', para: '/animais' },
          ...(editando ? [{ rotulo: editando.name, para: `/animais/${editando.id}` }] : []),
          { rotulo: editando ? 'Editar' : 'Novo animal' },
        ]}
        sobretitulo={haras.find((h) => h.id === f.owner_farm_id)?.name}
        titulo={editando ? `Editar ${editando.name}` : 'Novo animal'}
        meta={<span>Campos com * são obrigatórios. Sempre a data de nascimento, nunca a idade.</span>}
      />

      {cadastrados.length > 0 && (
        <p role="status" className="border-l-2 border-ok bg-ok/8 px-4 py-2 text-sm">
          <strong className="font-semibold">{cadastrados.at(-1)}</strong> cadastrado.{' '}
          {cadastrados.length > 1 && `${cadastrados.length} animais cadastrados nesta sessão. `}
          Preencha o próximo.
        </p>
      )}

      {tentou && <ResumoErros erros={erros} />}

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Painel titulo="Identificação">
          <div className="space-y-4">
            <CampoTexto
              ref={nome}
              rotulo="Nome"
              obrigatorio
              autoFocus={!id}
              value={f.name}
              onChange={(e) => mudar('name', e.target.value)}
              erro={erro('name')}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto
                rotulo="Registro ABCCMM"
                value={f.abccmm_registry}
                onChange={(e) => mudar('abccmm_registry', e.target.value)}
                erro={erro('abccmm_registry')}
              />
              <CampoTexto rotulo="Livro (stud book)" value={f.stud_book} onChange={(e) => mudar('stud_book', e.target.value)} />
            </div>
            <CampoTexto
              rotulo="Microchip"
              inputMode="numeric"
              value={f.microchip}
              onChange={(e) => mudar('microchip', e.target.value)}
            />
          </div>
        </Painel>

        <Painel titulo="Dados básicos">
          <div className="space-y-4">
            <OpcoesRadio
              rotulo="Sexo"
              obrigatorio
              valor={f.sex}
              onMudar={(v) => mudar('sex', v)}
              opcoes={[
                { valor: 'macho_inteiro', rotulo: 'Macho inteiro' },
                { valor: 'castrado', rotulo: 'Castrado' },
                { valor: 'femea', rotulo: 'Fêmea' },
              ]}
              erro={erro('sex')}
            />
            <div className="space-y-2">
              {f.birth_date_estimated ? (
                <CampoTexto
                  rotulo="Ano de nascimento"
                  obrigatorio
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="ex.: 2015"
                  value={f.ano_estimado}
                  onChange={(e) => mudar('ano_estimado', e.target.value.replace(/\D/g, ''))}
                  erro={erro('birth_date')}
                  ajuda="Gravado como data aproximada (1º de julho do ano)."
                />
              ) : (
                <CampoTexto
                  rotulo="Data de nascimento"
                  obrigatorio
                  type="date"
                  max={hoje}
                  value={f.birth_date}
                  onChange={(e) => mudar('birth_date', e.target.value)}
                  erro={erro('birth_date')}
                />
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={f.birth_date_estimated}
                  onChange={(e) => mudar('birth_date_estimated', e.target.checked)}
                  className="size-4 min-h-0 accent-(--color-marca)"
                />
                Data aproximada (só o ano é conhecido)
              </label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Selecao rotulo="Pelagem" value={f.pelagem} onChange={(e) => mudar('pelagem', e.target.value)}>
                <option value="">Não informada</option>
                {PELAGENS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
                <option value="outra">Outra…</option>
              </Selecao>
              {f.pelagem === 'outra' && (
                <CampoTexto rotulo="Qual pelagem" value={f.pelagem_outra} onChange={(e) => mudar('pelagem_outra', e.target.value)} />
              )}
            </div>
            <OpcoesRadio
              rotulo="Marcha declarada"
              valor={f.marcha_type_declared}
              onMudar={(v) => mudar('marcha_type_declared', v)}
              opcoes={[
                { valor: 'batida', rotulo: 'Batida' },
                { valor: 'picada', rotulo: 'Picada' },
                { valor: 'nao_informada', rotulo: 'Não informada' },
              ]}
            />
          </div>
        </Painel>

        <Painel titulo="Propriedade">
          <div className="space-y-4">
            <Selecao
              rotulo="Haras proprietário"
              obrigatorio
              value={f.owner_farm_id}
              onChange={(e) => mudar('owner_farm_id', e.target.value)}
              erro={erro('owner_farm_id')}
            >
              {haras.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Selecao>
            <Selecao rotulo="Haras criador" value={f.criador} onChange={(e) => mudar('criador', e.target.value)}>
              <option value="mesmo">O mesmo do proprietário</option>
              {haras
                .filter((h) => h.id !== f.owner_farm_id)
                .map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
            </Selecao>
          </div>
        </Painel>

        <Painel titulo="Genealogia">
          <div className="space-y-5">
            <EscolhaDePai
              papel="pai"
              animais={animais}
              valor={f.sire_id}
              proprio={id}
              erro={erro('sire_id')}
              onEscolher={(v) => mudar('sire_id', v)}
            />
            <EscolhaDePai
              papel="mae"
              animais={animais}
              valor={f.dam_id}
              proprio={id}
              erro={erro('dam_id')}
              onEscolher={(v) => mudar('dam_id', v)}
            />
          </div>
        </Painel>

        <Painel titulo="Observações" className="lg:col-span-2">
          <Campo rotulo="Observações">
            {(a) => (
              <textarea
                {...a}
                rows={3}
                value={f.notes}
                onChange={(e) => mudar('notes', e.target.value)}
                className={`${classeCampo()} resize-y`}
              />
            )}
          </Campo>
        </Painel>
      </div>

      {confirmarAvisos && avisos.length > 0 && (
        <div role="alert" className="border-l-2 border-aviso bg-aviso/8 px-4 py-3 text-sm">
          <p className="font-semibold text-aviso">Confira antes de salvar:</p>
          <ul className="mt-1 list-inside list-disc">
            {avisos.map((a) => (
              <li key={a.mensagem}>{a.mensagem}</li>
            ))}
          </ul>
          <p className="mt-2 text-apagado">Se estiver certo, salve de novo para confirmar.</p>
        </div>
      )}

      <BarraAcoes
        aviso={
          editando ? (
            <Botao variante="discreto" tamanho="sm" onClick={arquivar} className="text-erro hover:bg-erro/8">
              Arquivar animal
            </Botao>
          ) : sujo ? (
            'Alterações não salvas'
          ) : undefined
        }
      >
        <BotaoLink variante="discreto" to={editando ? `/animais/${editando.id}` : '/animais'}>
          Cancelar
        </BotaoLink>
        {!editando && (
          <Botao onClick={() => void salvar(true)} carregando={salvando}>
            {/* No celular a versao curta, para os tres botoes caberem numa linha. */}
            <span className="sm:hidden">Salvar e novo</span>
            <span className="hidden sm:inline">
              {confirmarAvisos ? 'Salvar mesmo assim e cadastrar outro' : 'Salvar e cadastrar outro'}
            </span>
          </Botao>
        )}
        <Botao type="submit" variante="principal" carregando={salvando}>
          {confirmarAvisos ? 'Salvar mesmo assim' : editando ? 'Salvar alterações' : 'Salvar'}
        </Botao>
      </BarraAcoes>
    </form>
  );
}

/**
 * Pai ou mae: pesquisa entre os cadastrados (filtrando pelo sexo) ou cadastro
 * rapido de quem vem de fora, com o ano de nascimento (data aproximada).
 */
function EscolhaDePai({
  papel,
  animais,
  valor,
  proprio,
  erro,
  onEscolher,
}: {
  papel: 'pai' | 'mae';
  animais: Animal[];
  valor: string | null;
  proprio: string | undefined;
  erro: string | undefined;
  onEscolher: (id: string | null) => void;
}) {
  const fonte = useFonte();
  const [rapido, setRapido] = useState(false);
  const [nome, setNome] = useState('');
  const [registro, setRegistro] = useState('');
  const [ano, setAno] = useState('');
  const [tentou, setTentou] = useState(false);
  const rotulo = papel === 'pai' ? 'Pai' : 'Mãe';
  const escolhido = animais.find((a) => a.id === valor) ?? null;

  const cadastrarRapido = async () => {
    setTentou(true);
    if (!nome.trim() || !anoValido(ano)) return;
    const idNovo = await fonte.cadastro!.salvarAnimal({
      name: nome.trim(),
      abccmm_registry: registro.trim() || null,
      stud_book: null,
      microchip: null,
      sex: papel === 'pai' ? 'macho_inteiro' : 'femea',
      birth_date: dataAproximada(Number(ano)),
      birth_date_estimated: true,
      coat_color: null,
      marcha_type_declared: null,
      // De fora da base: sem haras proprietario, nao entra no plantel de ninguem.
      owner_farm_id: null,
      breeder_farm_id: null,
      sire_id: null,
      dam_id: null,
      notes: null,
    });
    onEscolher(idNovo);
    setRapido(false);
    setNome('');
    setRegistro('');
    setAno('');
    setTentou(false);
  };

  return (
    <div className="space-y-2">
      <BuscaAnimal
        rotulo={rotulo}
        animais={animais}
        valor={escolhido}
        excluir={proprio ?? null}
        apenas={(a) => (papel === 'pai' ? a.sex !== 'femea' : a.sex === 'femea')}
        onEscolher={(a) => onEscolher(a?.id ?? null)}
      />
      {erro && <p className="text-xs text-erro">{erro}</p>}
      {!escolhido && !rapido && (
        <button
          type="button"
          onClick={() => setRapido(true)}
          className="min-h-0 text-[13px] font-medium text-destaque-texto underline-offset-2 hover:underline"
        >
          {papel === 'pai' ? 'Pai' : 'Mãe'} não está cadastrad{papel === 'pai' ? 'o' : 'a'}? Cadastrar rápido
        </button>
      )}
      {rapido && (
        <div className="space-y-3 border border-borda bg-superficie-2 p-3">
          <p className="text-xs text-apagado">
            Cadastro rápido de {papel === 'pai' ? 'um pai' : 'uma mãe'} de fora da base. Só o ano de nascimento: a data
            fica marcada como aproximada.
          </p>
          <CampoTexto
            rotulo="Nome"
            obrigatorio
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            erro={tentou && !nome.trim() ? 'Informe o nome.' : undefined}
          />
          <div className="grid grid-cols-2 gap-3">
            <CampoTexto rotulo="Registro ABCCMM" value={registro} onChange={(e) => setRegistro(e.target.value)} />
            <CampoTexto
              rotulo="Ano de nascimento"
              obrigatorio
              inputMode="numeric"
              maxLength={4}
              value={ano}
              onChange={(e) => setAno(e.target.value.replace(/\D/g, ''))}
              erro={tentou && !anoValido(ano) ? 'Ano com 4 dígitos.' : undefined}
            />
          </div>
          <div className="flex gap-2">
            <Botao tamanho="sm" variante="principal" onClick={() => void cadastrarRapido()}>
              Cadastrar e escolher
            </Botao>
            <Botao tamanho="sm" variante="discreto" onClick={() => setRapido(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </div>
  );
}
